import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  Clock,
  Sparkles,
  Tag
} from "lucide-react";

import { getPostBySlug, getPosts } from "@/lib/blog-api";


const SITE = "https://brightpath-hero.lovable.app";



const ease = [0.16, 1, 0.3, 1];

function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const category = post?._embedded?.["wp:term"]?.[0]?.[0]?.name || "Blog";
  const [related, setRelated] = useState([]);
  const tags = post?._embedded?.["wp:term"]?.[0]?.map((t) => t.name) || [];

  useEffect(() => {
    setLoading(true);
    getPostBySlug(slug).then((data) => {
      setPost(data);
      getPosts().then((posts) => {
        setRelated(
          posts.filter((p) => p.slug !== data?.slug)
        );
      });
    }).finally(() => {
      setLoading(false);
    });
  }, [slug]);
  console.log(post?._embedded?.["wp:term"]);
  const visual = {
    gradient: "from-slate-100 to-slate-200",
    accent: "#cbd5e1"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto max-w-3xl px-6 pt-32 pb-24">
          <div className="h-8 w-24 animate-pulse rounded bg-slate-200"></div>

          <div className="mt-6 h-16 w-full animate-pulse rounded bg-slate-200"></div>

          <div className="mt-4 h-8 w-3/4 animate-pulse rounded bg-slate-200"></div>

          <div className="mt-8 h-6 w-40 animate-pulse rounded bg-slate-200"></div>

          <div className="mt-10 aspect-[16/9] animate-pulse rounded-3xl bg-slate-200"></div>

          <div className="mt-10 space-y-4">
            <div className="h-4 animate-pulse rounded bg-slate-200"></div>
            <div className="h-4 animate-pulse rounded bg-slate-200"></div>
            <div className="h-4 animate-pulse rounded bg-slate-200"></div>
            <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200"></div>
          </div>
        </main>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-32 pb-24">
        <article className="container mx-auto max-w-3xl px-6 text-[12px] text-ink-soft">
          <Link to="/" className="hover:text-ink">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/blog" className="hover:text-ink">Blog</Link>
          <span className="mx-2">/</span>
          <span className="text-ink">
            {category}
          </span>
          <Header />

          <motion.header
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease }}
          >
            <div
              className="inline-flex items-center mt-6 gap-1 rounded-full border border-border bg-surface px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-ink-soft"
            >
              <Tag className="h-3 w-3" />
              {category}
            </div>
            <h1 className="mt-5 font-display text-4xl leading-[1.1] tracking-tight text-ink md:text-5xl">
              {post?.title.rendered}
            </h1>
            <div
              className="mt-5 text-lg leading-relaxed text-ink-soft"
              dangerouslySetInnerHTML={{
                __html: post?.excerpt.rendered || ""
              }}
            />

            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-border py-4 text-[13px] text-ink-soft">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <time dateTime={post?.date || ""}>
                  {post ? formatDate(post.date) : ""}
                </time>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {post ? getReadMinutes(post.content.rendered) : 0} min read
              </span>
            </div>
          </motion.header>

          {/* Hero image placeholder */}
          <motion.figure
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, ease, delay: 0.15 }}
            className={`relative mt-10 aspect-[16/9] overflow-hidden rounded-3xl border border-border bg-gradient-to-br ${visual.gradient} shadow-elevated`}
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.18]"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.6) 1px, transparent 0)",
                backgroundSize: "22px 22px",
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full blur-3xl"
              style={{ background: visual.accent, opacity: 0.45 }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-20 -left-10 h-72 w-72 rounded-full bg-white/40 blur-3xl"
            />
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.9, ease, delay: 0.35 }}
              className="absolute inset-0 grid place-items-center"
            >
              <div className="absolute inset-0">
                <img
                  src={
                    post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url
                  }
                  alt={post?.title.rendered}
                  className="h-full w-full object-cover"
                />
              </div>
            </motion.div>
            <div className="absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-background/85 px-3 py-1 text-[11px] font-medium text-ink backdrop-blur">
              <Sparkles className="h-3 w-3 text-brand" /> {category}
            </div>
            {/* <figcaption className="absolute bottom-4 right-5 rounded-full bg-background/80 px-2.5 py-1 text-[10.5px] uppercase tracking-wider text-ink-soft backdrop-blur">
              Cover art
            </figcaption> */}
          </motion.figure>


          <div
            className="prose prose-neutral mt-10 max-w-none"
            dangerouslySetInnerHTML={{
              __html: post?.content.rendered || ""
            }}
          />

          <div className="mt-14 flex flex-wrap gap-4 border-t border-border pt-8">
            {tags.map((tag) => (
              <span
                key={tag}
                className="text-[13px] text-ink-soft hover:text-ink"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div className="mt-10">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to all articles
            </Link>
          </div>
        </article>

        {related.length > 0 && (
          <section className="container mx-auto mt-24 max-w-6xl px-6">
            <div className="flex items-end justify-between gap-4">
              <div>
                <span className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">
                  More from the blog
                </span>
                <h2 className="mt-2 font-display text-3xl tracking-tight text-ink md:text-4xl">
                  Keep reading
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden text-sm font-medium text-ink-soft hover:text-ink md:inline-flex"
              >
                All articles →
              </Link>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((p, i) => {
                const category = p._embedded?.["wp:term"]?.[0]?.[0]?.name || "Blog";
                const v = getCategoryVisual(category);
                return (
                  <motion.div
                    key={p.slug}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, ease, delay: i * 0.08 }}
                  >
                    <Link
                      to={`/blog/${p.slug}`}
                      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-surface-elevated transition duration-500 hover:-translate-y-1 hover:border-ink/20 hover:shadow-elevated"
                    >
                      <div
                        className={`relative aspect-[16/10] overflow-hidden bg-gradient-to-br ${v.gradient}`}
                      >
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full blur-3xl"
                          style={{ background: v.accent, opacity: 0.4 }}
                        />
                        <div className="absolute inset-0 grid place-items-center text-[64px] transition-transform duration-700 group-hover:scale-110">
                          <img
                            src={p._embedded?.["wp:featuredmedia"]?.[0]?.source_url}
                            className="h-full w-full object-cover"
                            alt={p.title.rendered}
                          />
                        </div>
                        <span className="absolute left-4 top-4 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-ink backdrop-blur">
                          {category}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="font-display text-[17px] leading-snug tracking-tight text-ink">
                          {p.title.rendered}
                        </h3>
                        <div
                          className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-ink-soft"
                          dangerouslySetInnerHTML={{
                            __html: p.excerpt.rendered
                          }}
                        />
                        <span className="mt-auto inline-flex items-center gap-1 pt-5 text-[13px] font-medium text-ink">
                          Read article
                          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </section>
        )}


        <section className="container mx-auto mt-24 max-w-6xl px-6">
          <div className="rounded-3xl bg-[#1E3F33] p-10 md:p-14 text-white overflow-hidden relative">

            <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-center md:justify-between">

              <div className="max-w-2xl">
                <span className="uppercase tracking-[0.2em] text-sm text-[#BFD1C6]">
                  Expert Gynecologic Oncology Care
                </span>

                <h2 className="mt-4 font-serif text-3xl md:text-5xl leading-tight">
                  Schedule a Consultation With
                  <br />
                  Dr. Neha Agarwal
                </h2>

                <p className="mt-5 text-[#DCE7E1] leading-relaxed">
                  Get expert guidance for ovarian, cervical, uterine and other
                  gynecologic cancers with personalized treatment and compassionate care.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                <Link
                  to="/book-appointment"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-[#1E3F33] hover:bg-[#F3F5F2] transition"
                >
                  Book Appointment
                  <ArrowUpRight className="h-4 w-4" />
                </Link>


              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function prettifySlug(slug) {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function getReadMinutes(html) {
  const text = html.replace(/<[^>]+>/g, "");
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
}

function getCategoryVisual(category) {
  return {
    gradient: "from-slate-100 to-slate-200",
    accent: "#cbd5e1"
  };
}

export default BlogPostPage;